import { useState } from "react";
import { CheckCircle, XCircle, Clock, ChevronDown, ChevronUp, Download } from "lucide-react";
import HeaderBar from "../students/components/headerBar";
import { useVerificationLogsQuery } from "../../hooks/queries/useVerificationQueries";

const StatusBadge = ({ status }) => {
  const map = {
    Success: { cls: "cht-bg-green-50 cht-text-green-700", icon: <CheckCircle size={13} /> },
    Failed:  { cls: "cht-bg-red-50 cht-text-red-700",   icon: <XCircle size={13} /> },
    Pending: { cls: "cht-bg-yellow-50 cht-text-yellow-700", icon: <Clock size={13} /> },
  };
  const { cls, icon } = map[status] || map.Pending;
  return (
    <span className={`cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-1 cht-rounded-full cht-text-xs cht-font-semibold ${cls}`}>
      {icon} {status}
    </span>
  );
};

const LogRow = ({ log }) => {
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async (e) => {
    e.stopPropagation();
    setDownloading(true);
    try {
      const token = localStorage.getItem("access");
      const res = await fetch(`${process.env.REACT_APP_API_URL}/verification/download/${log.id}/pdf/`, {
        headers: { Authorization: `JWT ${token}` },
      });
      if (!res.ok) {
        throw new Error("Failed to download PDF");
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `verification_report_${log.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert("Failed to download document. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <tr
        className="cht-border-b cht-border-gray-100 hover:cht-bg-gray-50/60 cht-cursor-pointer cht-transition-colors"
        style={{ height: 56 }}
        onClick={() => setOpen((o) => !o)}
      >
        <td className="cht-px-5 cht-py-3 cht-text-sm cht-font-medium cht-text-gray-900">{log.service?.name ?? "—"}</td>
        <td className="cht-px-5 cht-py-3 cht-text-sm cht-text-gray-500">{new Date(log.created_at).toLocaleString()}</td>
        <td className="cht-px-5 cht-py-3">
          <StatusBadge status={log.status} />
        </td>
        <td className="cht-px-5 cht-py-3" onClick={(e) => e.stopPropagation()}>
          {log.status === "Success" ? (
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-3 cht-py-1.5 cht-bg-[#3e57da]/10 hover:cht-bg-[#3e57da]/20 disabled:cht-opacity-50 cht-text-[#3e57da] cht-rounded-lg cht-text-xs cht-font-semibold cht-transition-colors"
              title="Download Verification Document"
            >
              {downloading ? (
                <span className="cht-animate-spin cht-h-3 cht-w-3 cht-border-2 cht-border-[#3e57da] cht-border-t-transparent cht-rounded-full" />
              ) : (
                <Download size={13} />
              )}
              {downloading ? "Downloading..." : "Download PDF"}
            </button>
          ) : (
            <span className="cht-text-gray-400">—</span>
          )}
        </td>
        <td className="cht-px-5 cht-py-3 cht-text-right cht-text-gray-400">
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </td>
      </tr>
      {open && (
        <tr className="cht-bg-[#f2f4fd]">
          <td colSpan={5} className="cht-px-5 cht-py-4">
            {log.status === "Success" && (
              <div className="cht-rounded-xl cht-p-4 cht-bg-green-50/60 cht-border cht-border-green-100/80 cht-text-green-800">
                <p className="cht-text-sm cht-font-bold cht-mb-1">Verification Successful</p>
                <p className="cht-text-xs cht-text-green-700/90">
                  The verification was processed successfully. You can download the report from the Action column.
                </p>
              </div>
            )}
            {log.status === "Failed" && (
              <div className="cht-rounded-xl cht-p-4 cht-bg-red-50 cht-border cht-border-red-100 cht-text-red-800">
                <p className="cht-text-sm cht-font-bold cht-mb-1">Verification Failed</p>
                <p className="cht-text-xs cht-text-red-700/90">
                  {log.response_data?.error || log.response_data?.message || "An error occurred during verification."}
                </p>
              </div>
            )}
            {log.status === "Pending" && (
              <div className="cht-rounded-xl cht-p-4 cht-bg-yellow-50 cht-border cht-border-yellow-100 cht-text-yellow-800">
                <p className="cht-text-sm cht-font-bold cht-mb-1">Verification Pending</p>
                <p className="cht-text-xs cht-text-yellow-700/90">
                  This transaction is currently processing. Please check back shortly.
                </p>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

const SkeletonRow = () => (
  <tr className="cht-border-b cht-border-gray-100 cht-animate-pulse" style={{ height: 56 }}>
    {[1, 2, 3, 4, 5].map((i) => (
      <td key={i} className="cht-px-5 cht-py-3">
        <div className="cht-h-3 cht-bg-gray-200 cht-rounded cht-w-3/4" />
      </td>
    ))}
  </tr>
);

const LogsPage = () => {
  const { data: logsData, isLoading: loading } = useVerificationLogsQuery();
  const logs = Array.isArray(logsData) ? logsData : logsData?.results ?? [];

  return (
    <section className="cht-animate-in cht-fade-in cht-duration-700">
      <HeaderBar pageHeader="Verification Logs" />
      <main className="cht-container cht-mx-auto cht-mb-12">
        <div className="cht-bg-white cht-rounded-squircle cht-shadow-soft cht-border cht-border-slate-200/80 cht-overflow-hidden">
          <div className="cht-overflow-x-auto">
            <table className="cht-w-full">
              <thead>
                <tr className="cht-border-b cht-border-gray-100 cht-bg-gray-50/60">
                  <th className="cht-px-5 cht-py-3.5 cht-text-left cht-text-xs cht-font-semibold cht-text-gray-500 cht-uppercase">Service</th>
                  <th className="cht-px-5 cht-py-3.5 cht-text-left cht-text-xs cht-font-semibold cht-text-gray-500 cht-uppercase">Date</th>
                  <th className="cht-px-5 cht-py-3.5 cht-text-left cht-text-xs cht-font-semibold cht-text-gray-500 cht-uppercase">Status</th>
                  <th className="cht-px-5 cht-py-3.5 cht-text-left cht-text-xs cht-font-semibold cht-text-gray-500 cht-uppercase">Action</th>
                  <th className="cht-px-5 cht-py-3.5" />
                </tr>
              </thead>
              <tbody>
                {loading
                  ? Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)
                  : logs.length > 0
                  ? logs.map((log, i) => <LogRow key={i} log={log} />)
                  : (
                    <tr>
                      <td colSpan={5} className="cht-text-center cht-py-16 cht-text-gray-500 cht-text-sm">
                        No verification logs yet.
                      </td>
                    </tr>
                  )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </section>
  );
};

export default LogsPage;
